import { Text, Box, Flex, Loader, useMantineTheme, Group } from '@mantine/core';
import { useMemo, useState, useCallback } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { StepCard } from './StepCard';
import { UploadPillButton } from '../edit/components/UploadPillButton';
import { useUploadFiles } from '../hooks/useUploadFile';
import { StepProps } from '../types';
import { useAllClientDocuments } from '../hooks/useAllClientDocuments';
import { SuitabilitySelectableList } from '../edit/components/SuitabilitySelectableList';
import { getClientId } from '../edit/utils/getClientId';
import { useFilteredItems } from '../hooks/useFilteredItems';
import { useItemSelectionMapping } from '../hooks/useItemSelectionMapping';

type DocumentFilter = 'all' | 'fact-find' | 'meeting-note' | 'user-upload' | 'other';

const FILTER_LABELS: Record<DocumentFilter, string> = {
  all: 'All',
  'fact-find': 'Fact Finds',
  'meeting-note': 'Meeting Notes',
  'user-upload': 'Client Uploads',
  other: 'Other',
};

const FILTER_TABS: readonly DocumentFilter[] = [
  'all',
  'fact-find',
  'meeting-note',
  'user-upload',
  'other',
] as const;

const matchesDocumentFilter = (
  documentType: string | undefined,
  filter: DocumentFilter,
): boolean => {
  if (filter === 'all') return true;
  if (filter === 'user-upload') return documentType === 'data-upload';
  if (filter === 'other') {
    return (
      documentType !== 'fact-find' &&
      documentType !== 'meeting-note' &&
      documentType !== 'meeting-agenda' &&
      documentType !== 'data-upload'
    );
  }
  return documentType === filter;
};

export function SupportDocumentationStep({ data, setData, filter }: StepProps) {
  const clientId = getClientId(data);
  const [documentFilter, setDocumentFilter] = useState<DocumentFilter>('all');

  const { documents, isLoading } = useAllClientDocuments(
    data.isFamily && data.family
      ? { type: 'family', familyId: data.family.id }
      : { type: 'client', clientId },
  );

  const items = useMemo(() => {
    if (isLoading) return [];

    return documents
      .filter(doc => !!doc.file)
      .map(doc => ({
        id: doc.id,
        file: doc.file!,
        title: doc.file!.fileName,
        description: `Uploaded: ${new Date(doc.file!.createdAt).toLocaleDateString()}`,
        documentType: doc.documentType,
      }));
  }, [documents, isLoading]);

  const theme = useMantineTheme();
  const selectedIds = useMemo(() => data.supportDocuments.map(d => d.id), [data.supportDocuments]);

  const familyId = data.isFamily ? data.family?.id : undefined;

  const { isUploading, handleFileUpload } = useUploadFiles({
    clientId,
    familyId,
    onUploaded: uploadedFiles => {
      setData(prev => ({
        ...prev,
        supportDocuments: [...prev.supportDocuments, ...uploadedFiles],
      }));
    },
  });

  const mapIdsToSelectedFiles = useItemSelectionMapping(items);

  const handleSelectionChange = useCallback(
    (ids: string[]) => {
      setData(prev => ({
        ...prev,
        supportDocuments: mapIdsToSelectedFiles(ids),
      }));
    },
    [mapIdsToSelectedFiles, setData],
  );

  const typeFilteredItems = useMemo(
    () => items.filter(item => matchesDocumentFilter(item.documentType, documentFilter)),
    [items, documentFilter],
  );
  const filteredItems = useFilteredItems(typeFilteredItems, filter);

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard
        title={`Add supporting documentation`}
        subTitle="Some documents you might want. Please confirm, select more or upload"
      >
        <Flex align="center" justify="space-between" w="100%" p="xs">
          <Group gap={0}>
            {FILTER_TABS.map(filterType => (
              <Text
                key={filterType}
                size="sm"
                fw={FONT_WEIGHTS.semibold}
                c={documentFilter === filterType ? 'orange.7' : 'gray.6'}
                bg={documentFilter === filterType ? 'orange.1' : undefined}
                px="xs"
                py="xxs"
                style={{ cursor: 'pointer', borderRadius: theme.radius.md }}
                onClick={() => setDocumentFilter(filterType)}
              >
                {FILTER_LABELS[filterType]}
              </Text>
            ))}
          </Group>

          <UploadPillButton loading={isUploading} onFilesSelected={handleFileUpload} />
        </Flex>
        <Box flex={1} mih={0} style={{ overflow: 'auto' }}>
          {isLoading ? (
            <Flex justify="center" align="center" h="100%">
              <Loader data-testid="loader" />
            </Flex>
          ) : (
            <SuitabilitySelectableList
              items={filteredItems}
              selectMultiple
              selectedItemIds={selectedIds}
              setSelectedItemIds={handleSelectionChange}
              showReviewed={false}
              showViewButton={false}
              dateLayout="inline"
              highlightedElement={data.highlightedElement}
              emptyView={<Text c="gray.6">No supporting documents found</Text>}
            />
          )}
        </Box>
      </StepCard>
    </Flex>
  );
}
